import React from "react";
import { useTranslation } from "react-i18next";

import Button from "@/components/button";
import Input from "@/components/input";
import { MonthYearPicker } from "@/components/month-year-picker";
import { notify } from "@/utils";
import { Logger } from "@/utils/logger";
import {
  ROLE_GROUPS,
  isWorkExperienceComplete,
} from "@/constants/onboarding";

import { useWorkerOnboarding } from "../-worker-onboarding-context";

interface Props {
  onContinue?: (handler: () => Promise<boolean>) => void;
}

export function WorkerRoleExperienceStep({ onContinue }: Props) {
  const { t } = useTranslation("onboarding");
  const {
    data,
    addWorkExperience,
    removeWorkExperience,
    setWorkExperienceField,
    toggleWorkExperienceRole,
    setNoExperience,
    saveDraft,
    invitedBy,
  } = useWorkerOnboarding();

  React.useEffect(() => {
    if (!onContinue) return;
    onContinue(async () => {
      // "New to Restaurants" (noExperience === true) sends an empty
      // roleExperiences array — no client-side validation needed for that path.
      if (!data.noExperience && data.workExperiences.length > 0) {
        const incompleteIndex = data.workExperiences.findIndex(
          (we) => !isWorkExperienceComplete(we),
        );
        if (incompleteIndex !== -1) {
          notify.error(t("workerRoleExperience.fillEachEntry"));
          return false;
        }
      }
      try {
        await saveDraft();
        return true;
      } catch (e) {
        Logger.error("worker onboarding save failed at experience", {
          error: e instanceof Error ? e.message : String(e),
        });
        notify.error(
          e instanceof Error ? e.message : t("workerRoleExperience.saveFailed"),
        );
        return false;
      }
    });
  }, [onContinue, data, saveDraft, t]);

  return (
    <section className="w-full flex flex-col gap-6">
      <div>
        <h2 className="heading-1">{t("workerRoleExperience.title")}</h2>
        <p className="body-small text-gray-600 mt-2">
          {t("workerRoleExperience.description")}
        </p>
        {invitedBy && !data.noExperience && (
          <p className="body-small mt-3 rounded-lg border-2 border-ember/30 bg-ember/5 p-3">
            {t("workerRoleExperience.prefilledFromInvite", {
              restaurant: invitedBy,
            })}
          </p>
        )}
      </div>
      {data.noExperience ? (
        <div className="rounded-lg ring-1 ring-garnet/20 p-4 flex flex-col gap-3">
          <h3 className="font-semibold text-gray-700">
            {t("workerRoleExperience.newToRestaurants")}
          </h3>
          <p className="body-small text-gray-600">
            {t("workerRoleExperience.newToRestaurantsBody")}
          </p>
          <div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setNoExperience(false)}
            >
              {t("workerRoleExperience.workedBeforeCta")}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setNoExperience(true)}
              className="w-full md:w-auto"
            >
              {t("workerRoleExperience.newCta")}
            </Button>
          </div>
          {data.workExperiences.map((we, index) => (
            <div
              key={index}
              className="rounded-lg ring-1 ring-garnet/20 p-4 flex flex-col gap-4"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-gray-700">
                  {t("workerRoleExperience.restaurantHeading", { index: index + 1 })}
                </h3>
                {data.workExperiences.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeWorkExperience(index)}
                    className="text-sm text-garnet underline hover:opacity-80"
                  >
                    {t("workerRoleExperience.remove")}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Input
                    label={t("workerRoleExperience.restaurantNameLabel")}
                    placeholder={t("workerRoleExperience.restaurantNamePlaceholder")}
                    value={we.restaurant}
                    onChange={(e) =>
                      setWorkExperienceField(
                        index,
                        "restaurant",
                        e.currentTarget.value,
                      )
                    }
                  />
                </div>
                <MonthYearPicker
                  label={t("workerRoleExperience.startMonth")}
                  value={we.start}
                  onChange={(v) =>
                    setWorkExperienceField(index, "start", v ?? "")
                  }
                />
                <MonthYearPicker
                  label={t("workerRoleExperience.endMonth")}
                  value={we.end}
                  onChange={(v) =>
                    setWorkExperienceField(index, "end", v ?? "")
                  }
                  disabled={we.current}
                />
                <label className="flex items-center gap-2 mt-2 md:col-span-2">
                  <input
                    type="checkbox"
                    checked={we.current}
                    onChange={(e) =>
                      setWorkExperienceField(
                        index,
                        "current",
                        e.currentTarget.checked,
                      )
                    }
                  />
                  <span className="body-small">
                    {t("workerRoleExperience.currentlyWorkHere")}
                  </span>
                </label>
              </div>
              <div className="flex flex-col gap-3">
                <h4 className="font-semibold text-gray-700">
                  {t("workerRoleExperience.rolesHeld")}
                </h4>
                {ROLE_GROUPS.map((g) => (
                  <div key={g.group} className="flex flex-col gap-2">
                    <span className="body-small text-gray-500">{g.group}</span>
                    <div className="flex flex-wrap gap-2">
                      {g.roles.map(([key, label, icon]) => {
                        const selected = we.roles.includes(key);
                        return (
                          <button
                            key={key}
                            type="button"
                            role="checkbox"
                            aria-pressed={selected}
                            aria-checked={selected}
                            onClick={() =>
                              toggleWorkExperienceRole(index, key)
                            }
                            className={
                              "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors " +
                              (selected
                                ? "bg-garnet text-white ring-1 ring-garnet"
                                : "bg-white text-garnet ring-1 ring-garnet/30 hover:ring-garnet")
                            }
                          >
                            <span aria-hidden>{icon}</span>
                            <span>{label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div>
            <Button
              type="button"
              variant="outline"
              onClick={addWorkExperience}
              className="w-full md:w-auto"
            >
              {t("workerRoleExperience.addAnother")}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
